import React, { FC } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { IEmployeeBalanceDTO } from 'src/api/dtos';
import { InfoFilledIcon, Tooltip } from 'src/shared/components';
import { DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { formatDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

import { AbsenceBalanceIcon } from './AbsenceBalanceIcon';

interface Props {
  balance: IEmployeeBalanceDTO;
  showAccrualBalanceInDays: boolean;
  isFutureBalanceCheckEnabled: boolean;
  isFutureBalanceViolationBlockEnabled: boolean;
  onViewDetails?: () => void;
  highlighted?: boolean;
}

export const AccrualBalanceSection: FC<Props> = ({
  balance,
  showAccrualBalanceInDays,
  isFutureBalanceCheckEnabled,
  isFutureBalanceViolationBlockEnabled,
  onViewDetails,
  highlighted,
}) => {
  const { t } = useTranslate();
  const durationFormat = useSelector(getDurationFormat);

  const currentBalanceDays =
    showAccrualBalanceInDays && balance.currentBalanceInDays
      ? `/ ${balance.currentBalanceInDays} ${t('global.days')}`
      : undefined;

  const expectedBalanceDays =
    showAccrualBalanceInDays && balance.expectedBalanceInDays
      ? `/ ${balance.expectedBalanceInDays} ${t('global.days')}`
      : undefined;

  const hasEmptyBalance = !balance.currentBalanceInSeconds && !balance.expectedBalanceInSeconds;
  const showFutureBalance = !hasEmptyBalance && isFutureBalanceCheckEnabled;

  return (
    <Stack spacing={2} direction="column" alignItems="flex-start">
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography component="span" sx={{ fontSize: '0.875rem' }} color="text.secondary">
                {t('global.status')}
              </Typography>
              <AbsenceBalanceIcon
                balance={balance}
                hideTooltip
                shouldShowError={isFutureBalanceViolationBlockEnabled}
              />
            </Stack>
          }
        />
        {!!balance.currentBalanceInSeconds && (
          <Chip
            size="small"
            variant="outlined"
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography component="span" sx={{ fontSize: 'inherit' }} color="text.secondary">
                  {t('accrualSummary.currentBalance')}
                </Typography>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                  {formatDuration(balance.currentBalanceInSeconds, durationFormat, 'seconds')} {currentBalanceDays}
                </Typography>
                <Tooltip
                  placement="top"
                  title={<Typography sx={{ fontSize: '0.75rem', fontWeight: 400 }}>{t('global.asOfToday')}</Typography>}
                >
                  <IconButton size="small" color="info">
                    <InfoFilledIcon size={16} />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
        )}
        {showFutureBalance && (
          <Chip
            size="small"
            variant="outlined"
            sx={{ borderColor: highlighted ? '#000' : undefined }}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography component="span" sx={{ fontSize: 'inherit' }} color="text.secondary">
                  {t('accrualSummary.expectedBalance')}
                </Typography>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                  {formatDuration(balance.expectedBalanceInSeconds, durationFormat, 'seconds')} {expectedBalanceDays}
                </Typography>
                <Tooltip
                  placement="top"
                  title={
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 400 }}>
                      {t('global.asOfDate', { date: moment(balance.date).format(DATE_FORMAT) })}
                    </Typography>
                  }
                >
                  <IconButton size="small" color="info">
                    <InfoFilledIcon size={16} />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
        )}
      </Stack>
      {showFutureBalance && !!onViewDetails && (
        <Button id="view-breakdown-btn" size="small" color="secondary" onClick={onViewDetails}>
          {t('absences.viewBreakdown')}
        </Button>
      )}
    </Stack>
  );
};
